import { ComponentType, SVGAttributes } from "react";

import {
  BigIntegerIcon,
  BooleanIcon,
  MeetingIcon,
  NumberIcon,
  ObjectIcon,
  QuestionIcon,
  TextStringIcon,
  TimeIcon,
} from "@hightouch/htui";
import * as yup from "yup";

import { ALL_COLUMN_OPTIONS } from "src/components/audiences/constants";
import { ColumnType } from "src/types/visual";

import { RealtimeEventColumnTypes } from "./types";

export const getColumnTypeLabel = (type: string) => {
  return (
    ALL_COLUMN_OPTIONS.find((option) => option.value === type)?.label ??
    "Unknown"
  );
};

export const getColumnTypeIcon = (type: string) => {
  return (
    ALL_COLUMN_OPTIONS.find((option) => option.value === type)?.icon ??
    QuestionIcon
  );
};

export const JSON_ARRAY_OPTIONS = [
  {
    label: "JSON Array (Strings)",
    value: ColumnType.JsonArrayStrings,
    icon: TextStringIcon,
  },
  {
    label: "JSON Array (Numbers)",
    value: ColumnType.JsonArrayNumbers,
    icon: NumberIcon,
  },
  {
    label: "Unknown JSON",
    value: ColumnType.Json,
    icon: ObjectIcon,
  },
];

export const REALTIME_EVENT_COLUMN_TYPE_OPTIONS = [
  { label: "Big Integer", value: RealtimeEventColumnTypes.BigInt },
  { label: "Boolean", value: RealtimeEventColumnTypes.Boolean },
  { label: "Date", value: RealtimeEventColumnTypes.Date },
  { label: "Number", value: RealtimeEventColumnTypes.Number },
  { label: "String", value: RealtimeEventColumnTypes.String },
  { label: "Timestamp", value: RealtimeEventColumnTypes.Timestamp },
  {
    label: "Array (Strings)",
    value: RealtimeEventColumnTypes.JsonArrayStrings,
  },
  {
    label: "Array (Numbers)",
    value: RealtimeEventColumnTypes.JsonArrayNumbers,
  },
  {
    label: "Array (Objects)",
    value: RealtimeEventColumnTypes.JsonArrayObjects,
  },
];

export const TypeIconMap: Record<
  RealtimeEventColumnTypes,
  ComponentType<SVGAttributes<SVGElement>>
> = {
  [RealtimeEventColumnTypes.BigInt]: BigIntegerIcon,
  [RealtimeEventColumnTypes.Boolean]: BooleanIcon,
  [RealtimeEventColumnTypes.Date]: MeetingIcon,
  [RealtimeEventColumnTypes.Number]: NumberIcon,
  [RealtimeEventColumnTypes.String]: TextStringIcon,
  [RealtimeEventColumnTypes.Timestamp]: TimeIcon,
  [RealtimeEventColumnTypes.JsonArrayStrings]: TextStringIcon,
  [RealtimeEventColumnTypes.JsonArrayNumbers]: NumberIcon,
  [RealtimeEventColumnTypes.JsonArrayObjects]: ObjectIcon,
};

export const RealtimeEventColumnSchema = yup.object({
  realtimeAudiences: yup
    .object({
      rtaColumns: yup
        .array()
        .of(
          yup.object({
            json_path: yup.string().required("JSON path is required"),
            alias: yup.string().nullable(),
            type: yup
              .string()
              .oneOf(Object.values(RealtimeEventColumnTypes))
              .required("Type is required"),
            isJoinKey: yup.boolean().required("Join key flag is required"),
            isTimestampColumn: yup
              .boolean()
              .required("Timestamp column flag is required"),
            itemFields: yup
              .array()
              .of(
                yup.object({
                  id: yup.string().required(),
                  path: yup
                    .string()
                    .required("Item field is required")
                    .test(
                      "one-level",
                      "Nested arrays are not supported",
                      (value) => !value?.includes("[]"),
                    ),
                  type: yup
                    .string()
                    .oneOf(
                      Object.values(RealtimeEventColumnTypes).filter(
                        (type) =>
                          type !== RealtimeEventColumnTypes.JsonArrayObjects,
                      ),
                    )
                    .required("Type is required"),
                }),
              )
              .optional(),
          }),
        )
        .required("Columns are required for a realtime audiences event"),
    })
    .required("Realtime audiences configuration is required"),
});
